import React from 'react';
import { Formik, Form, Field } from 'formik';
import { useRouter } from 'next/router';

import { IconSearch, devices } from '@maiia/design-system';
import { Box, Button, Typography } from '@mui/material';

import { styled, css, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from '@/src/i18n';
import { APP_ROUTES, ENTIRE_FRANCE_DISTANCE_MAX } from '@/src/constants';
import SuggestionInput from '@/components/molecules/Formik/SuggestionInput';

const Container = styled(Box)(
  ({ theme }) => css`
    display: flex;
    height: ${theme.spacing(6.75)};
    width: 100%;
  `,
);

const SearchButton = styled(Button)(
  ({ theme }) => css`
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    min-width: ${theme.spacing(6.5)};
    width: ${theme.spacing(8)};
    font-size: 44px;
    padding-left: ${theme.spacing(3.5)};
    ${theme.breakpoints.up('sm')} {
      min-width: ${theme.spacing(11)};
    }
  `,
);

const SearchContainer = styled(Box)(
  ({ theme }) => css`
    display: flex;
    flex: 1;
    background-color: ${theme.palette.background.secondary};
    border-radius: 50px 0 0 50px;
    margin: auto;
    box-shadow: 0px 10px 36px rgb(0 0 0 / 10%);
    padding: ${theme.spacing(0.8, 0.5, 0.5, 3)};
    height: 100%;

    .input-field {
      flex: 1;
      font-size: ${theme.spacing(2)};
    }

    @media ${devices.mobile} {
      .dropdown-autocomplete {
        display: none;
      }
    }

    ${theme.breakpoints.up('md')} {
      margin-right: ${theme.spacing(1)};
      .input-field {
        font-size: ${theme.spacing(2.5)};
        width: 100%;
      }
    }

    .input-dropdown {
      width: 100%;
      position: relative;
    }
    .dropdown-autocomplete {
      width: 100%;
      margin-left: ${theme.spacing(-3.25)};
    }
    .input-wrapper:after {
      border-bottom: none;
      position: unset;
    }
  `,
);

const TlcSearchbarSection = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // This callback exist to handle "speciality" and "expertise" suggestions
  // other types are handled in their item component
  const onChangeCallback = React.useCallback(shortName => {
    if (!shortName) return;
    const query = {
      speciality: shortName,
      locality: '75000-PARIS',
      distanceMax: ENTIRE_FRANCE_DISTANCE_MAX,
      consultationModeFilter: 'TLC',
    };
    router.push({
      pathname: APP_ROUTES.SPECIALITY.LOCALITY,
      query,
    });
  }, []);

  return (
    <>
      <Typography
        variant="h3"
        component="h1"
        sx={{
          textAlign: 'center',
          color: 'primary.contrastText',
          marginBottom: 2.5,
          marginTop: { md: 2.5 },
        }}
      >
        {t('search_bar_header')}
      </Typography>
      {/* Formik is an overkill for this use case, refactor to use MUI Autocomplete,
      no need to submit, since the user will be redirected on choosing a suggestion */}
      <Formik initialValues={{ suggestion: undefined }} onSubmit={() => {}}>
        <Form>
          <Container
            onClick={() => {
              if (isMobile)
                router.push('/search/suggestions?speciality-only=true');
            }}
          >
            <SearchContainer>
              <Field
                name="suggestion"
                placeholder={t('search_bar_find_a_specialty')}
                component={SuggestionInput}
                className="with-icon"
                onChangeCallback={onChangeCallback}
              />
            </SearchContainer>
            <SearchButton
              variant="contained"
              color="secondary"
              type="submit"
              sx={{
                '& .MuiButton-startIcon svg': {
                  fontSize: {
                    xs: '36px',
                    sm: '44px',
                  },
                },
              }}
              startIcon={<IconSearch />}
            />
          </Container>
        </Form>
      </Formik>
    </>
  );
};

export default TlcSearchbarSection;
